<template>
  <div class="parts-library" :class="{ 'parts-library--browsing': showResults }">
    <p class="library-intro">Find a part to review or edit, or browse by category.</p>
    <div class="library-search-row">
      <div class="library-search">
        <label class="sr-only" for="inventory-search">Search parts by name, brand, model or ASIN</label>
        <span class="icon icon-magnifying-glass" aria-hidden="true"></span>
        <input id="inventory-search" ref="search" v-model="search" type="search" autocomplete="off" :disabled="loading || !!error" placeholder="Search all parts by name, brand, model or ASIN…" @keydown.esc="clearSearch" />
        <button v-if="search" type="button" class="library-clear" aria-label="Clear parts search" @click="clearSearch">&times;</button>
      </div>
      <button type="button" class="library-refresh" :disabled="loading" @click="loadLibrary"><span class="icon icon-cycle" aria-hidden="true"></span> {{ loading ? 'Loading…' : 'Refresh' }}</button>
    </div>
    <div v-if="error" class="library-error" role="alert">{{ error }} <button type="button" @click="loadLibrary">Try again</button></div>
    <div v-else class="library-meta" aria-live="polite">
      <span v-if="loading">Loading catalog…</span>
      <template v-else><span><strong>{{ items.length.toLocaleString() }}</strong> parts across {{ categories.length }} categories</span><span class="library-meta-note">Includes unavailable and archived parts</span></template>
    </div>
    <div class="library-categories" aria-label="Part categories" :aria-busy="loading ? 'true' : 'false'">
      <button v-for="category in categories" :key="category.type" type="button" class="library-category" :class="{ active: selectedCategory === category.type }" :aria-pressed="selectedCategory === category.type ? 'true' : 'false'" @click="selectCategory(category.type)">
        <span class="library-category-head"><span class="library-category-icon icon" :class="category.icon" aria-hidden="true"></span><strong>{{ category.label }}</strong><span class="library-category-count">{{ loading || error ? '—' : categoryCounts[category.type].toLocaleString() }}</span></span>
        <span class="library-category-description">{{ category.description }}</span>
        <span class="library-category-foot">Browse {{ category.label.toLowerCase() }} <span aria-hidden="true">&rarr;</span></span>
      </button>
    </div>
    <section v-if="showResults && !loading && !error" ref="results" class="library-results" aria-labelledby="inventory-results-title">
      <div class="library-results-head">
        <h2 id="inventory-results-title" aria-live="polite">{{ filteredItems.length.toLocaleString() }} {{ filteredItems.length === 1 ? 'part' : 'parts' }}<span v-if="activeCategory"> · {{ activeCategory.label }}</span></h2>
        <div class="library-results-actions">
          <router-link v-if="activeCategory" :to="categoryPath(activeCategory.type)">Manage category <span aria-hidden="true">&rarr;</span></router-link>
          <button type="button" @click="resetSearch">Clear filters</button>
        </div>
      </div>
      <p v-if="!filteredItems.length" class="library-empty">No parts match{{ search.trim() ? ' “' + search.trim() + '”' : ' this category' }}. Try another name or ASIN.</p>
      <ul v-else class="library-result-list">
        <li v-for="part in visibleItems" :key="part.type + ':' + part.id">
          <router-link :to="editPath(part)" class="library-result" :aria-label="'Edit ' + part.title">
            <span class="library-result-icon icon" :class="categoryFor(part.type).icon" aria-hidden="true"></span>
            <span class="library-result-copy"><strong>{{ part.title }}</strong><small>{{ categoryFor(part.type).label }}<span v-if="part.asin"> · {{ part.asin }}</span></small></span>
            <span v-if="part.archived" class="library-result-status">Archived</span><span v-else-if="!part.available" class="library-result-status">Unavailable</span>
            <span class="library-result-edit">Edit <span aria-hidden="true">&rarr;</span></span>
          </router-link>
        </li>
      </ul>
      <button v-if="visibleItems.length < filteredItems.length" type="button" class="library-more" @click="visibleLimit += 24">Show more <span>({{ visibleItems.length }} of {{ filteredItems.length }})</span></button>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'

const CATEGORIES = [
  { type: 'cpu', label: 'Processors', icon: 'icon-gauge', description: 'Cores, clocks, sockets and memory support.' },
  { type: 'gpu', label: 'Graphics', icon: 'icon-controller-play', description: 'Graphics cards, video memory and performance.' },
  { type: 'motherboard', label: 'Motherboards', icon: 'icon-grid', description: 'Sockets, chipsets, expansion and form factors.' },
  { type: 'ram', label: 'Memory', icon: 'icon-drive', description: 'RAM kits, capacities, generations and speeds.' },
  { type: 'storage', label: 'Storage', icon: 'icon-box', description: 'SSDs and hard drives, interfaces and capacities.' },
  { type: 'case', label: 'Cases', icon: 'icon-archive', description: 'Form factors, component clearance and airflow.' },
  { type: 'psu', label: 'Power supplies', icon: 'icon-flash', description: 'Wattage, efficiency and modular connections.' },
]
const normalize = value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

export default {
  name: 'SelectCategory',
  data(){
    return { categories: CATEGORIES, items: [], loading: true, error: '', search: String(this.$route.query.q || ''), selectedCategory: '', visibleLimit: 24, routeTimer: null, requestPending: false }
  },
  computed: {
    categoryCounts(){
      const counts = {}
      CATEGORIES.forEach(category => { counts[category.type] = 0 })
      this.items.forEach(part => { counts[part.type]++ })
      return counts
    },
    activeCategory(){ return CATEGORIES.find(category => category.type === this.selectedCategory) },
    showResults(){ return !!(this.search.trim() || this.selectedCategory) },
    filteredItems(){
      const words = normalize(this.search).split(' ').filter(Boolean)
      return this.items.filter(part => (!this.selectedCategory || part.type === this.selectedCategory) && words.every(word => part.searchText.includes(word)))
    },
    visibleItems(){ return this.filteredItems.slice(0, this.visibleLimit) },
  },
  watch: {
    search(){ this.updateLocation() },
    selectedCategory(){ this.updateLocation() },
    '$route.query': { immediate: true, handler(query){
      this.search = String(query.q || '')
      this.selectedCategory = CATEGORIES.some(category => category.type === query.category) ? query.category : ''
    } },
  },
  mounted(){ this.loadLibrary() },
  beforeDestroy(){ clearTimeout(this.routeTimer) },
  methods: {
    async loadLibrary(){
      if(this.requestPending) return
      this.requestPending = true
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(apiDomain + 'catalog-parts/library-index', { headers: { Authorization: 'Bearer ' + localStorage.usertoken } })
        if(!data || !Array.isArray(data.items)) throw new Error('Invalid catalog response')
        this.items = data.items.filter(part => CATEGORIES.some(category => category.type === part.type)).map(part => {
          const brand = String(part.brand || '').trim()
          const model = String(part.model || '').trim()
          const title = (brand && !model.toLowerCase().startsWith(brand.toLowerCase()) ? brand + ' ' : '') + (model || 'Unnamed part')
          return { ...part, title, searchText: normalize([brand, model, part.asin, part.id, this.categoryFor(part.type).label].join(' ')) }
        }).sort((a, b) => a.title.localeCompare(b.title) || a.type.localeCompare(b.type) || a.id - b.id)
      } catch(error){
        this.error = 'The parts catalog could not be loaded.'
      } finally {
        this.loading = false
        this.requestPending = false
      }
    },
    categoryFor(type){ return CATEGORIES.find(category => category.type === type) || CATEGORIES[0] },
    categoryPath(type){ return { path: '/users/items-controller/items', query: { type: type === 'gpu' ? 'graphics' : type } } },
    editPath(part){ return { path: '/users/items-controller/editpart', query: { type: part.type, id: part.id } } },
    selectCategory(type){
      this.selectedCategory = this.selectedCategory === type ? '' : type
      this.$nextTick(() => { if(this.$refs.results) this.$refs.results.scrollIntoView({ behavior: 'smooth', block: 'nearest' }) })
    },
    clearSearch(){ this.search = ''; this.$refs.search.focus() },
    resetSearch(){ this.search = ''; this.selectedCategory = ''; this.$refs.search.focus() },
    updateLocation(){
      this.visibleLimit = 24
      clearTimeout(this.routeTimer)
      this.routeTimer = setTimeout(() => {
        const query = { ...this.$route.query }
        if(this.search.trim()) query.q = this.search; else delete query.q
        if(this.selectedCategory) query.category = this.selectedCategory; else delete query.category
        this.$router.replace({ path: this.$route.path, query }).catch(() => {})
      }, 200)
    },
  },
}
</script>

<style scoped>
.parts-library { color: #cfd2da; }
.library-intro { margin: 0 0 20px; color: #a4acbc; font-size: 14px; }
.library-search-row { display: flex; gap: 10px; }
.library-search { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; padding: 0 14px; border: 1px solid #434857; border-radius: 7px; background: #0b111a; }
.library-search:focus-within { border-color: #1997c6; box-shadow: 0 0 0 2px #1997c61f; }
.library-search > .icon { color: #1997c6; }
.library-search input { width: 100%; min-width: 0; height: 44px; border: 0; outline: none; background: transparent; color: #e7ebf3; font: inherit; font-size: 13px; }
.library-search input::-webkit-search-cancel-button { display: none; }
.library-search input::placeholder { color: #8f98aa; }
.library-clear { padding: 4px; border: 0; background: transparent; color: #a4acbc; font-size: 20px; cursor: pointer; }
.library-refresh { display: flex; align-items: center; gap: 7px; padding: 0 14px; border: 1px solid #434857; border-radius: 7px; background: transparent; color: #a4acbc; font-size: 12px; cursor: pointer; }
.library-refresh:disabled { opacity: .5; cursor: wait; }
.library-meta { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px; margin: 14px 0 22px; color: #a4acbc; font-size: 12px; }
.library-meta strong { color: #e7ebf3; }
.library-meta-note { color: #8f98aa; }
.library-categories { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.library-category { display: grid; gap: 12px; min-width: 0; padding: 18px; border: 1px solid #434857; border-radius: 8px; background: linear-gradient(135deg, #0e1821a6, #080c12a6); color: #cfd2da; text-align: left; font: inherit; cursor: pointer; }
.library-category:hover, .library-category.active { border-color: #1997c6; background: #0b202bcc; }
.library-category-head { display: flex; align-items: center; gap: 10px; }
.library-category-head strong { font-size: 14px; font-weight: 600; }
.library-category-icon { color: #1997c6; font-size: 19px; }
.library-category-count { margin-left: auto; color: #7fd0ee; font-size: 20px; font-variant-numeric: tabular-nums; }
.library-category-description { min-height: 36px; color: #8f98aa; font-size: 12px; line-height: 1.5; }
.library-category-foot { display: flex; justify-content: space-between; color: #7fd0ee; font-size: 11px; }
.parts-library--browsing .library-categories { display: flex; flex-wrap: wrap; gap: 8px; }
.parts-library--browsing .library-category { padding: 9px 12px; }
.parts-library--browsing .library-category-description, .parts-library--browsing .library-category-foot { display: none; }
.parts-library--browsing .library-category-head { gap: 8px; }
.parts-library--browsing .library-category-head strong, .parts-library--browsing .library-category-count { font-size: 12px; }
.parts-library--browsing .library-category-icon { font-size: 14px; }
.library-results { margin-top: 26px; }
.library-results-head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; }
.library-results h2 { margin: 0; color: #cfd2da; font-size: 16px; }
.library-results h2 span { color: #8f98aa; font-weight: 400; }
.library-results-actions { display: flex; gap: 16px; font-size: 12px; }
.library-results-actions a, .library-results-actions button, .library-error button { padding: 0; border: 0; background: transparent; color: #7fd0ee; font: inherit; cursor: pointer; }
.library-result-list { list-style: none; margin: 0; padding: 0; border: 1px solid #434857; border-radius: 8px; overflow: hidden; }
.library-result-list li + li { border-top: 1px solid #43485780; }
.library-result { display: flex; align-items: center; gap: 14px; padding: 13px 16px; background: #0b111a99; color: #cfd2da; text-decoration: none; }
.library-result:hover, .library-result:focus-visible { background: #1997c61a; color: #e7ebf3; text-decoration: none; }
.library-result-icon { width: 22px; color: #1997c6; }
.library-result-copy { display: grid; gap: 4px; min-width: 0; flex: 1; }
.library-result-copy strong { font-size: 13px; font-weight: 500; overflow-wrap: anywhere; }
.library-result-copy small { color: #8f98aa; font-size: 11px; }
.library-result-status { color: #b7a67c; font-size: 10px; }
.library-result-edit { color: #7fd0ee; white-space: nowrap; font-size: 12px; }
.library-empty, .library-error { padding: 20px 0; color: #a4acbc; font-size: 13px; }
.library-more { display: block; margin: 14px auto 0; padding: 9px 14px; border: 1px solid #434857; border-radius: 6px; background: transparent; color: #7fd0ee; font-size: 12px; cursor: pointer; }
.library-more span { color: #8f98aa; margin-left: 6px; }
.parts-library button:focus-visible, .parts-library a:focus-visible { outline: 2px solid #1997c6; outline-offset: 3px; }
@media (max-width: 1100px) { .library-categories { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 600px) {
  .library-categories { grid-template-columns: 1fr; }
  .library-category { gap: 9px; padding: 15px; }
  .library-category-description { min-height: 0; }
  .library-refresh { padding: 0 9px; }
  .library-result { gap: 9px; padding: 12px; flex-wrap: wrap; }
  .library-result-status { margin-left: 31px; }
  .library-result-copy { flex-basis: calc(100% - 40px); }
  .library-result-edit { margin-left: auto; }
}
</style>
